import { useEffect, useState, useRef } from "react";

export default function AdSenseCard({ adSlot, fullWidth = false, adType = "default" }) {
  const [adLoaded, setAdLoaded] = useState(false);
  const [adError, setAdError] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const [isLocalhost, setIsLocalhost] = useState(false);
  const adRef = useRef(null);
  const timeoutRef = useRef(null);

  // Check if we're on localhost for testing
  useEffect(() => {
    const hostname = window.location.hostname;
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('localhost');
    setIsLocalhost(isLocal);
    
    if (isLocal) {
      console.log('🧪 LOCALHOST DETECTED: Showing test placeholder instead of real ads');
    }
  }, []);

  // Ad configurations - Updated with working slots
  const getAdConfig = () => {
    if (fullWidth) {
      // Banner ad configuration - Working banner
      return {
        adSlot: "5406069373",
        layoutKey: "+2t+ra+p-4r+cl",
        style: { display: "block" },
        dataAdFormat: "fluid"
      };
    } else {
      // In-feed/Sidebar ad configuration - Use new in-feed slot
      return {
        adSlot: "5382120277", // New in-feed ad slot
        layoutKey: "+2t+ra+p-4r+cl", // Same layout key as banner
        style: { display: "block" },
        dataAdFormat: "fluid"
      };
    }
  };

  useEffect(() => {
    // Skip AdSense loading on localhost - just show test placeholder
    if (isLocalhost) {
      console.log('📍 Localhost: Showing test ad placeholder');
      setAdLoaded(true);
      return;
    }

    const loadAd = async () => {
      try {
        // Wait a moment to ensure the DOM is ready
        await new Promise(resolve => setTimeout(resolve, 200));

        // Check if AdSense is available
        if (typeof window !== 'undefined' && window.adsbygoogle) {
          // Set a timeout to show fallback if ad doesn't load within 8 seconds (increased for mobile)
          timeoutRef.current = setTimeout(() => {
            if (!adLoaded) {
              console.log("Ad timeout reached, hiding ad container");
              setShowFallback(true);
            }
          }, 8000);

          try {
            window.adsbygoogle.push({});
            console.log("AdSense push successful for slot:", adSlot);
          } catch (error) {
            console.error("AdSense push error:", error);
            setAdError(true);
            setShowFallback(true);
          }
        } else {
          console.warn("AdSense not available");
          setShowFallback(true);
        }
      } catch (error) {
        console.error("AdSense loading error:", error);
        setAdError(true);
        setShowFallback(true);
      }
    };

    loadAd();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [adSlot, isLocalhost]);

  // Observer to detect if ad content loaded (skip on localhost)
  useEffect(() => {
    if (!adRef.current || isLocalhost) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          const hasAdContent = Array.from(mutation.addedNodes).some(node => 
            node.nodeType === Node.ELEMENT_NODE && 
            (node.tagName === 'IFRAME' || 
             node.querySelector('iframe') ||
             node.classList?.contains('adsbygoogle-noablate') ||
             (node.style && (node.style.minHeight || node.style.height)))
          );
          
          if (hasAdContent) {
            console.log("Ad content detected, marking as loaded");
            setAdLoaded(true);
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
          }
        }
      });
    });

    observer.observe(adRef.current, { 
      childList: true, 
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    return () => observer.disconnect();
  }, [isLocalhost]);

  // Don't render anything if we should show fallback
  if (showFallback && !adLoaded && !isLocalhost) {
    console.log("Hiding ad container due to fallback");
    return null;
  }

  const adConfig = getAdConfig();

  // Test placeholder component for localhost
  const TestAdPlaceholder = ({ type }) => (
    <div className={`bg-gradient-to-r from-blue-100 to-purple-100 border-2 border-dashed border-blue-300 rounded-lg flex items-center justify-center ${
      type === 'banner' ? 'h-20 sm:h-24 w-full' : 'h-48 sm:h-64 w-full'
    }`}>
      <div className="text-center p-2 sm:p-4">
        <div className="text-blue-600 font-semibold mb-1 text-xs sm:text-sm">
          🧪 TEST AD PLACEHOLDER
        </div>
        <div className="text-xs text-gray-600">
          {type === 'banner' ? 'Banner Ad' : 'In-feed Ad'} - Slot: {adConfig.adSlot}
        </div>
        <div className="text-xs text-gray-500 mt-1">
          Real ads will appear in production
        </div>
      </div>
    </div>
  );

  if (fullWidth) {
    return (
      <div className="w-full mb-4 sm:mb-6 bg-white rounded-lg shadow-md p-2 sm:p-4 border-2 border-dashed border-gray-200">
        <div className="text-center text-xs text-gray-400 mb-2">
          {isLocalhost ? 'Test Advertisement' : 'Advertisement'}
        </div>
        
        {isLocalhost ? (
          <TestAdPlaceholder type="banner" />
        ) : (
          <div className="w-full">
            <div ref={adRef} className="w-full max-h-[120px] overflow-hidden"> {/* Limit banner height */}
              <ins
                className="adsbygoogle"
                style={adConfig.style}
                data-ad-format={adConfig.dataAdFormat}
                data-ad-layout-key={adConfig.layoutKey}
                data-ad-client="ca-pub-6341522368354250"
                data-ad-slot={adConfig.adSlot}
              />
            </div>
            {!adLoaded && !showFallback && (
              <div className="flex items-center justify-center h-20 sm:h-24 bg-gray-100 rounded animate-pulse">
                <span className="text-gray-400 text-sm">Loading advertisement...</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white border-2 border-dashed border-gray-200 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 h-full min-w-[250px]"> {/* Ensure minimum 250px width */}
      <div className="p-2 sm:p-3 h-full flex flex-col justify-center items-center min-h-[280px] sm:min-h-[320px] relative">
        <div className="text-center text-xs text-gray-400 mb-2 sm:mb-3">
          {isLocalhost ? 'Test Advertisement' : 'Advertisement'}
        </div>
        
        {isLocalhost ? (
          <TestAdPlaceholder type="infeed" />
        ) : (
          <div className="w-full flex-1 flex items-center justify-center min-w-[250px]"> {/* Ensure minimum 250px width */}
            <div ref={adRef} className="w-full h-full min-h-[200px] sm:min-h-[250px] min-w-[250px]">
              <ins
                className="adsbygoogle"
                style={adConfig.style}
                data-ad-format={adConfig.dataAdFormat}
                data-ad-layout-key={adConfig.layoutKey}
                data-ad-client="ca-pub-6341522368354250"
                data-ad-slot={adConfig.adSlot}
              />
            </div>
            {!adLoaded && !showFallback && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-50 rounded animate-pulse">
                <div className="text-center">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-2"></div>
                  <span className="text-gray-400 text-xs">Loading ad...</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}